import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, finalize } from 'rxjs';

export interface BackgroundTask {
    id: string;
    title: string;
    message: string;
    status: 'running' | 'success' | 'error';
    returnRoute?: string;
    returnLabel?: string;
    createdAt: Date;
}

@Injectable({ providedIn: 'root' })
export class BackgroundTaskService {
    private readonly tasksSubject = new BehaviorSubject<BackgroundTask[]>([]);
    tasks$ = this.tasksSubject.asObservable();

    private taskIdCounter = 0;

    /**
     * Adiciona uma nova tarefa em execução
     */
    addTask(config: {
        title: string;
        message: string;
        returnRoute?: string;
        returnLabel?: string;
    }): string {
        const taskId = `task-${++this.taskIdCounter}-${Date.now()}`;
        const task: BackgroundTask = {
            id: taskId,
            title: config.title,
            message: config.message,
            status: 'running',
            returnRoute: config.returnRoute,
            returnLabel: config.returnLabel,
            createdAt: new Date()
        };

        const currentTasks = this.tasksSubject.value;
        this.tasksSubject.next([...currentTasks, task]);

        return taskId;
    }

    /**
     * Atualiza o status de uma tarefa para sucesso
     */
    completeTask(taskId: string, successMessage?: string): void {
        this.updateTaskStatus(taskId, 'success', successMessage);
        // Não remove automaticamente - aguarda o usuário fechar
    }

    /**
     * Atualiza o status de uma tarefa para erro
     */
    failTask(taskId: string, errorMessage?: string): void {
        this.updateTaskStatus(taskId, 'error', errorMessage);
        // Não remove automaticamente - aguarda o usuário fechar
    }

    /**
     * Remove uma tarefa da lista
     */
    removeTask(taskId: string): void {
        const currentTasks = this.tasksSubject.value;
        this.tasksSubject.next(currentTasks.filter(t => t.id !== taskId));
    }

    /**
     * Atualiza o status e mensagem de uma tarefa
     */
    private updateTaskStatus(taskId: string, status: BackgroundTask['status'], message?: string): void {
        const currentTasks = this.tasksSubject.value;
        const updatedTasks = currentTasks.map(task => {
            if (task.id === taskId) {
                return {
                    ...task,
                    status,
                    message: message || task.message
                };
            }
            return task;
        });
        this.tasksSubject.next(updatedTasks);
    }

    /**
     * Wrapper para executar um Observable como tarefa em background
     */
    runAsBackgroundTask<T>(
        observable: Observable<T>,
        config: {
            title: string;
            runningMessage: string;
            successMessage: string;
            errorMessage: string;
            returnRoute?: string;
            returnLabel?: string;
        }
    ): Observable<T> {
        const taskId = this.addTask({
            title: config.title,
            message: config.runningMessage,
            returnRoute: config.returnRoute,
            returnLabel: config.returnLabel
        });

        return new Observable<T>(subscriber => {
            observable.subscribe({
                next: (value) => {
                    this.completeTask(taskId, config.successMessage);
                    subscriber.next(value);
                    subscriber.complete();
                },
                error: (error) => {
                    const errorMsg = error?.error?.message || error?.message || config.errorMessage;
                    this.failTask(taskId, errorMsg);
                    subscriber.error(error);
                }
            });
        });
    }
}
